import {
  type PasteTarget,
  thunkPasteFromClipboard,
} from '@store/migrationActions/thunkActions/thunkClipboardActions'
import { ErrorNotification } from '@storeOld/reducers/notification'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import type { AppState } from '@/types'
import { Keys } from '../keyboard'

const CLIPBOARD_UNAVAILABLE_TEXT =
  'Clipboard is unavailable. Allow clipboard access in the site settings or use Paste in the toolbar menu.'

const useOnPasteKeyboardActions = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  const selectedNodeLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedNodeLink
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // sticky note selectors
  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )

  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  // section selectors
  const selectedSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionId
  )

  const selectedToEditSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionEditingId
  )

  // color picker
  const colorPickerIsOpen = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.colorPickerIsOpen
  )

  const resolveTarget = (): PasteTarget | null => {
    if (colorPickerIsOpen) {
      return null
    }

    if (selectedBlock && !selectedToEditBlock) {
      return 'block'
    }

    if (selectedNode && !selectedToEditNode && !selectedNodeLink) {
      return 'node'
    }

    if (selectedStickyNote && !selectedToEditStickyNote) {
      return 'stickyNote'
    }

    if (selectedSection && !selectedToEditSection) {
      return 'section'
    }

    return null
  }

  const paste = async (target: PasteTarget) => {
    const result = await dispatch(thunkPasteFromClipboard(target))

    if (result === 'CLIPBOARD_UNAVAILABLE') {
      dispatch(ErrorNotification(CLIPBOARD_UNAVAILABLE_TEXT))
    }
  }

  return (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey) || event.code !== Keys.Paste) {
      return
    }

    const target = resolveTarget()

    if (!target) {
      return
    }

    event.preventDefault()
    paste(target)
  }
}

export default useOnPasteKeyboardActions
